import { clsx } from 'clsx'
import { Gap, Space } from 'component/Space'
import { Typo } from 'component/Typo'
import { Vertical } from 'component/Vertical'
import { useLatestCallback } from 'hook/useLatestCallback'
import { i18n } from 'i18n'
import { Icon } from 'icon/Icon'
import { observer } from 'mobx-react-lite'
import { ComboButton } from 'page/catalog/buttons/ComboButton'
import { ImagePane } from 'page/catalog/ItemCard/ImagePane'
import { ItemActions } from 'page/catalog/ItemCard/ItemActions'
import { StandActions } from 'page/catalog/ItemCard/StandActions'
import { useDemo } from 'page/catalog/store/CatalogContext'
import { itemPreview, ItemPreviewStore } from 'page/catalog/store/ItemPreviewStore'
import { useRef } from 'react'
import { ui } from 'store/home/ui/HomeUiStore'
import { opt } from 'store/opt'
import { formatPrice, Item } from 'type/Item'
import { getTranslation } from 'util/translations'
import css from './ItemCard.module.scss'

type Props = {
  item: Item
}

export const ItemCard = observer(({ item }: Props) => {
  const { mobile } = ui.window
  const demo = useDemo()
  const { show_name, stand, tsum, animated, basic } = demo
  const { articles, brands, colors, prices } = demo.product.items ?? {}
  const { product_name, article, brand_name, dress_colors } = item
  const f1 = stand ? 11 : 9
  const f2 = stand ? 13 : 11
  const className = clsx(css.item, animated && css.animated, opt.square && css.square)
  const mix = demo.canMix(item)
  const combo = mobile && !stand && !opt.baga && demo.canCombine(item)

  const ref = useRef<HTMLDivElement>(null)

  const price = formatPrice(item.prices)
  const weight = tsum ? 500 : 600

  const onClickImage = useLatestCallback(() => {
    itemPreview.open(new ItemPreviewStore(item))
  })

  return (
    <Vertical ref={ref} className={className}>
      <Vertical onClick={onClickImage}>
        <Space className={css.image}>
          <ImagePane item={item} />
          {combo && <ComboButton item={item} className={css.combo} />}
        </Space>
        {mix && <Space height={0} center>
          <Space className={clsx(css.mix, basic && css.basic)} gap={4}>
            <Icon size={basic ? 16 : 12} name="ai" />
            <Typo size={basic ? 12 : 10} one medium upper>
              {i18n('CanBeCombined')}
            </Typo>
          </Space>
        </Space>}
        <Gap height={12} />
        <Vertical gap={0} oh>
          {articles && article && (<Space gap={4} center height={24}>
            <Space>
              <Icon size={16} name="barcode" secondary tertiary={stand} />
            </Space>
            <Typo size={f1} line={16} upper secondary tertiary={stand} wrap>
              {article}
            </Typo>
          </Space>)}
          {((brands && brand_name) || show_name) && <Vertical gap={2}>
            {brands && brand_name && <Typo size={f1} upper secondary tertiary={stand} center wrap>
              {brand_name}
            </Typo>}
            {show_name && <Typo size={f2} weight={weight} first upper center wrap>
              {product_name}
            </Typo>}
          </Vertical>}
          {colors && dress_colors && (
            <Typo size={f1} upper center wrap>
              {getTranslation('colors', dress_colors)}
            </Typo>
          )}
          {prices && price && (
            <Typo size={f2} weight={weight} first primary={tsum} center>
              {price}
            </Typo>
          )}
        </Vertical>
      </Vertical>
      <Gap height={12} spring />
      {stand ? <StandActions item={item} /> : <ItemActions item={item} />}
    </Vertical>
  )
})
